import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { Eye, EyeOff, Sparkles, Zap, Bot, BarChart2, ArrowRight, Check, Loader2 } from 'lucide-react'
import { Lockup } from '../components/Lockup'
import { GoogleIcon } from '../components/GoogleIcon'

const features = [
  { icon: Sparkles, text: 'Campaña lista en 5 minutos con IA' },
  { icon: Zap, text: 'Lanza en Meta Ads y Google Ads' },
  { icon: Bot, text: 'Agente IA que responde clientes 24/7' },
  { icon: BarChart2, text: 'Pega tu web → la IA crea todo por ti' },
]

export default function Login() {
  const navigate = useNavigate()
  const { login, signInWithGoogle } = useAuth()
  const [searchParams] = useSearchParams()
  const [form, setForm] = useState({ email: '', password: '' })
  const [showPass, setShowPass] = useState(false)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState('')
  const [showReset, setShowReset] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetSent, setResetSent] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)

  const handleGoogleLogin = async () => {
    setGoogleLoading(true)
    setError('')
    const next = searchParams.get('next') || '/dashboard'
    const { error } = await signInWithGoogle(`${window.location.origin}${next}`)
    if (error) {
      setError(`No pudimos conectar con Google: ${error.message}`)
      setGoogleLoading(false)
    }
    // En éxito la página redirige al provider, no reseteamos googleLoading
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (!form.email || !form.password) { setError('Rellena todos los campos'); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('Introduce un email válido'); return }
    setLoading(true)
    try {
      await login(form.email, form.password)
      navigate('/dashboard')
    } catch (err) {
      const msg = err.message || ''
      if (msg === 'Invalid login credentials' || /invalid.*credentials/i.test(msg)) {
        setError('Email o contraseña incorrectos')
      } else if (/email.*not.*confirm/i.test(msg)) {
        setError('Debes confirmar tu email antes de iniciar sesión')
      } else if (/rate.*limit|too many/i.test(msg)) {
        setError('Demasiados intentos. Espera unos minutos')
      } else {
        setError(msg || 'Error al iniciar sesión')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = async (e) => {
    e.preventDefault()
    if (!resetEmail.trim()) return
    setResetLoading(true)
    try {
      await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/dashboard`,
      })
      setResetSent(true)
    } catch {
      setResetSent(true) // siempre mostramos éxito por seguridad
    } finally {
      setResetLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-ink flex flex-col lg:flex-row">

      {/* Panel izquierdo — solo desktop */}
      <div className="hidden lg:flex flex-col gap-12 w-1/2 aurora-bg-subtle border-r border-line p-12 relative">
        <div className="relative z-10">
          <Lockup size="lg" kicker="PUBLICIDAD · IA · 24/7" kineticSettle />
        </div>

        <div className="relative z-10">
          <span className="chip-cyan mb-5">✦ AI · ADS · 24/7</span>
          <h2 className="font-display text-5xl text-white mb-4 leading-[0.95]">
            La IA que<br />
            <span className="aurora-text italic">respira</span> por<br />
            tu negocio.
          </h2>
          <p className="text-muted mb-10 text-base leading-relaxed max-w-md">
            Genera anuncios, gestiona campañas y responde clientes — todo desde un solo lugar.
          </p>

          <div className="flex flex-col gap-4">
            {features.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-aurora-violet/15 rounded-lg flex items-center justify-center flex-shrink-0 border border-line-violet/30">
                  <Icon size={15} className="text-aurora-violet-soft" />
                </div>
                <p className="text-white/80 text-sm">{text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3 relative z-10">
          <div className="bg-surface-2 rounded-2xl p-5 border border-line">
            <p className="text-white/85 text-sm leading-relaxed mb-3">
              "Con ClixyAds generamos en minutos lo que antes nos llevaba horas. El copy de IA suena exactamente como nosotros."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full aurora-gradient flex items-center justify-center text-xs font-bold text-white">M</div>
              <div>
                <p className="text-white text-xs font-medium">María García</p>
                <p className="text-muted text-xs">Directora de Marketing · Clínica Salud+</p>
              </div>
            </div>
          </div>
          <div className="bg-surface-2 rounded-2xl p-5 border border-line">
            <p className="text-white/85 text-sm leading-relaxed mb-3">
              "Pegué mi web y en 5 minutos tenía 3 anuncios listos para lanzar. Nunca había sido tan fácil."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-aurora-cyan flex items-center justify-center text-xs font-bold text-ink">J</div>
              <div>
                <p className="text-white text-xs font-medium">Jorge Martínez</p>
                <p className="text-muted text-xs">Fundador · FitZone Gym</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Panel derecho — formulario */}
      <div className="flex-1 flex flex-col lg:items-center lg:justify-center p-6 py-12 lg:py-6 min-h-screen lg:min-h-0">
        <div className="w-full max-w-sm mx-auto">

          {/* Logo móvil */}
          <div className="lg:hidden flex justify-center mb-10">
            <Lockup size="md" kicker="PUBLICIDAD · IA" />
          </div>

          {!showReset ? (
            <>
              <div className="mb-8">
                <h1 className="font-display text-3xl text-white mb-1">Bienvenido</h1>
                <p className="text-muted text-sm">Entra en tu cuenta para continuar</p>
              </div>

              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={googleLoading || loading}
                className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed text-gray-900 border border-line py-3 rounded-xl font-medium transition-all text-sm"
              >
                {googleLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin text-gray-700" />
                    <span>Conectando con Google...</span>
                  </>
                ) : (
                  <>
                    <GoogleIcon size={18} />
                    <span>Continuar con Google</span>
                  </>
                )}
              </button>

              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-line" />
                <span className="text-xs text-dim uppercase tracking-wider">o</span>
                <div className="flex-1 h-px bg-line" />
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                  <label className="block text-xs text-muted mb-2">Email</label>
                  <input
                    type="email"
                    placeholder="tu@email.com"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full bg-surface border border-line rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-aurora-cyan focus:ring-2 focus:ring-aurora-cyan/20 transition-all text-sm"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-muted">Contraseña</label>
                    <button
                      type="button"
                      onClick={() => { setShowReset(true); setResetEmail(form.email) }}
                      className="text-xs text-aurora-violet-soft hover:text-white transition-all"
                    >
                      ¿Olvidaste la contraseña?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      className="w-full bg-surface border border-line rounded-xl px-4 py-3 pr-12 text-white placeholder-gray-600 focus:outline-none focus:border-aurora-cyan focus:ring-2 focus:ring-aurora-cyan/20 transition-all text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-dim hover:text-white transition-all"
                    >
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 aurora-gradient hover:opacity-90 disabled:opacity-40 text-white py-3.5 rounded-xl font-medium transition-all mt-1 text-sm"
                >
                  {loading ? 'Entrando...' : <><span>Entrar</span><ArrowRight size={16} /></>}
                </button>
              </form>

              <p className="text-center text-dim text-sm mt-6">
                ¿No tienes cuenta?{' '}
                <Link to="/register" className="text-aurora-violet-soft hover:text-white font-medium">
                  Regístrate gratis
                </Link>
              </p>
            </>
          ) : (
            /* Panel recuperar contraseña */
            <>
              <div className="mb-8">
                <button
                  onClick={() => { setShowReset(false); setResetSent(false) }}
                  className="text-xs text-dim hover:text-white transition-all mb-4"
                >
                  ← Volver al login
                </button>
                <h1 className="text-2xl font-bold text-white mb-1">Recuperar contraseña</h1>
                <p className="text-muted text-sm">Te enviaremos un enlace para crear una nueva</p>
              </div>

              {resetSent ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-5 text-center">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Check size={22} className="text-green-400" />
                  </div>
                  <p className="text-green-400 font-semibold mb-1">Correo enviado</p>
                  <p className="text-muted text-sm">
                    Si existe una cuenta con ese email, recibirás un enlace en unos minutos. Revisa también el spam.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleReset} className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs text-muted mb-2">Email de tu cuenta</label>
                    <input
                      type="email"
                      placeholder="tu@email.com"
                      value={resetEmail}
                      onChange={e => setResetEmail(e.target.value)}
                      className="w-full bg-surface border border-line rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-aurora-cyan focus:ring-2 focus:ring-aurora-cyan/20 transition-all text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={resetLoading || !resetEmail.trim()}
                    className="w-full aurora-gradient hover:opacity-90 disabled:opacity-40 text-white py-3.5 rounded-xl font-medium transition-all text-sm"
                  >
                    {resetLoading ? 'Enviando...' : 'Enviar enlace'}
                  </button>
                </form>
              )}
            </>
          )}

          {/* Hero + features + testimonios (solo móvil, debajo del form) */}
          <div className="lg:hidden mt-16 pt-10 border-t border-line-soft">
            <span className="chip-cyan mb-4">✦ AI · ADS · 24/7</span>
            <h2 className="font-display text-3xl text-white mt-3 mb-3 leading-[1]">
              La IA que<br />
              <span className="aurora-text italic">respira</span> por tu negocio.
            </h2>
            <p className="text-muted text-sm leading-relaxed mb-8">
              Genera anuncios, gestiona campañas y responde clientes — todo desde un solo lugar.
            </p>

            <div className="flex flex-col gap-3 mb-10">
              {features.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-aurora-violet/15 rounded-lg flex items-center justify-center flex-shrink-0 border border-aurora-violet/30">
                    <Icon size={15} className="text-aurora-violet-soft" />
                  </div>
                  <p className="text-white/80 text-sm">{text}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3">
              <div className="bg-surface-2 rounded-2xl p-5 border border-line">
                <p className="text-white/85 text-sm leading-relaxed mb-3">
                  "Con ClixyAds generamos en minutos lo que antes nos llevaba horas. El copy de IA suena exactamente como nosotros."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full aurora-gradient flex items-center justify-center text-xs font-bold text-white">M</div>
                  <div>
                    <p className="text-white text-xs font-medium">María García</p>
                    <p className="text-muted text-xs">Directora de Marketing · Clínica Salud+</p>
                  </div>
                </div>
              </div>
              <div className="bg-surface-2 rounded-2xl p-5 border border-line">
                <p className="text-white/85 text-sm leading-relaxed mb-3">
                  "Pegué mi web y en 5 minutos tenía 3 anuncios listos para lanzar. Nunca había sido tan fácil."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-aurora-cyan flex items-center justify-center text-xs font-bold text-ink">J</div>
                  <div>
                    <p className="text-white text-xs font-medium">Jorge Martínez</p>
                    <p className="text-muted text-xs">Fundador · FitZone Gym</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
